import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Mic, User, Calendar, Star, Ticket, ChevronRight, MessageCircle, Theater } from 'lucide-react';

const StandUpComedy: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "School of Comedy Arts";
    
    return (
        <div className="min-h-screen bg-[#1c1c1c] font-sans text-slate-100 selection:bg-red-600 selection:text-white">
            {/* Top Bar */}
            <div className="bg-red-700 text-white py-2 px-6 text-xs font-bold text-center tracking-wide uppercase">
                Fall Registration Open • Early Bird Discounts Available
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-[#1c1c1c]/95 backdrop-blur border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-red-700 shadow-md">
                            <Mic size={20} />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-white uppercase">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8 font-medium text-sm text-slate-300">
                        <a href="#" className="hover:text-red-500 transition-colors">Classes</a>
                        <a href="#" className="hover:text-red-500 transition-colors">Shows</a>
                        <a href="#" className="hover:text-red-500 transition-colors">Faculty</a>
                        <a href="#" className="hover:text-red-500 transition-colors">About</a>
                    </div>

                    <div className="flex gap-4">
                        <Button variant="ghost" className="hidden md:flex text-white hover:bg-white/10 font-bold">
                            Student Portal
                        </Button>
                        <Button className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-md px-6 shadow-md transition-all">
                            Enroll Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative py-28 px-6 bg-[#181818] overflow-hidden">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-red-500 text-xs font-bold uppercase tracking-wider mb-6">
                            <Star size={12} fill="currentColor" /> Since 1985
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6 tracking-tight">
                            Find Your <br/>
                            <span className="text-red-600">Voice.</span>
                        </h1>
                        <p className="text-xl text-slate-400 mb-10 max-w-lg leading-relaxed">
                            The world's premier training ground for stand-up, improv, and sketch comedy. Learn from the legends.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button className="h-14 px-8 bg-white text-black hover:bg-slate-200 font-bold rounded-md text-base">
                                View Class Schedule
                            </Button>
                            <Button variant="outline" className="h-14 px-8 border-white/20 text-white hover:bg-white/10 font-bold rounded-md text-base">
                                See Alumni
                            </Button>
                        </div>
                    </div>

                    {/* Hero Graphic */}
                    <div className="relative">
                        <div className="aspect-square bg-gradient-to-br from-red-900/20 to-black rounded-full absolute -top-10 -right-10 w-96 h-96 blur-3xl opacity-50"></div>
                        <div className="relative bg-[#222] border border-white/10 rounded-2xl p-1 shadow-2xl skew-y-[-2deg]">
                             <div className="aspect-video bg-[#000] rounded-xl overflow-hidden relative">
                                 <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516280440614-6697288d5d38?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-70"></div>
                                 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                                 <div className="absolute bottom-6 left-6 grid gap-1">
                                     <div className="bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 w-fit rounded">Live</div>
                                     <div className="text-white font-bold text-lg">Main Stage: Student Showcase</div>
                                 </div>
                             </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Curriculum */}
            <section className="py-24 px-6 bg-[#141414]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-white mb-4">Curriculum</h2>
                        <div className="w-16 h-1 bg-red-600 mx-auto rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: "Writing Lab", level: "Level 1-3", desc: "Structure, setup, punchline. The mechanics of joke writing." },
                            { title: "Performance", level: "All Levels", desc: "Stage presence, microphone technique, and crowd work." },
                            { title: "Industry Business", level: "Advanced", desc: "Getting booked, agent representation, and touring." }
                        ].map((item, i) => (
                            <div key={i} className="bg-[#1c1c1c] p-8 rounded-xl border border-white/5 hover:border-red-600/50 transition-colors group cursor-pointer">
                                <div className="flex justify-between items-start mb-6">
                                     <h3 className="text-xl font-bold text-white max-w-[70%]">{item.title}</h3>
                                     <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 border border-white/10 px-2 py-1 rounded">{item.level}</span>
                                </div>
                                <p className="text-slate-400 leading-relaxed mb-8 text-sm">{item.desc}</p>
                                <div className="flex items-center text-red-500 font-bold text-sm group-hover:gap-2 transition-all">
                                    <span>Syllabus</span>
                                    <ChevronRight size={16} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#111] text-white py-20 px-6 border-t border-white/5">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                     <div className="col-span-1 md:col-span-1">
                        <div className="flex items-center gap-2 mb-6">
                            <Mic size={24} className="text-red-600" />
                            <span className="font-bold text-lg uppercase">{schoolName}</span>
                        </div>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Developing comedic voices for stage and screen.
                        </p>
                    </div>
                     <div>
                        <h4 className="font-bold text-slate-300 mb-4 text-sm uppercase tracking-wider">School</h4>
                        <ul className="space-y-3 text-sm text-slate-500">
                            <li><a href="#" className="hover:text-white transition-colors">Our History</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Diversity & Inclusion</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Locations</a></li>
                        </ul>
                    </div>
                     <div>
                        <h4 className="font-bold text-slate-300 mb-4 text-sm uppercase tracking-wider">Students</h4>
                        <ul className="space-y-3 text-sm text-slate-500">
                            <li><a href="#" className="hover:text-white transition-colors">Calendar</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Box Office</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Open Mics</a></li>
                        </ul>
                    </div>
                     <div>
                        <h4 className="font-bold text-slate-300 mb-4 text-sm uppercase tracking-wider">Contact</h4>
                        <ul className="space-y-3 text-sm text-slate-500">
                            <li>classes@comedyarts.edu</li>
                            <li>(555) HAHA-123</li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/5 text-center text-xs text-slate-600">
                    &copy; 2026 {schoolName}.
                </div>
            </footer>
        </div>
    );
};

export default StandUpComedy;
